import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getAntiforgeryToken, portalFetch, PortalHttpError } from "../../api/http";
import { useEnhancedLoad } from "../../shell/Shell";
import { ErrorSummary, FieldError, SectionMessage } from "../../ui/feedback";
import { useFocusHeading } from "../cuenta/focusHeading";
import { MobileBack } from "./TagsPage";

type Participant = {
  participantId: number;
  kind: string;
  userId?: number | null;
  agentId?: number | null;
  role: string;
  name: string;
};
type Candidate = { id: number; name: string };
type Page = {
  caseGroupId: number;
  title: string;
  status: string;
  canManage: boolean;
  participants: Participant[];
  humans: Candidate[];
  agents: Candidate[];
};
type Result = { saved: boolean; failureCode?: string | null; page?: Page | null };

const textKeys = "GRP_,CHAT_TITLE,EMP_UNAVAILABLE,ACTION_CANCEL,TEAM_ERR_FORBIDDEN,CHAT_NOT_FOUND";

/**
 * Participantes de un grupo interno (/portal/chats/{id}/participantes), como Participantes.razor: participantes, agregar
 * personas y empleados IA, renombrar y cerrar. Solo quien administra el grupo (lo decide Messaging) ve los formularios; cada
 * cambio vuelve a validarse en el servidor. Como el POST clásico de la página, después de guardar la vista vuelve arriba.
 */
export function ParticipantsPage({ locale, id }: { locale: string; id: number }) {
  const texts = useQuery({
    queryKey: ["texts", locale, "participants"],
    queryFn: () =>
      portalFetch<Record<string, string>>(`/api/portal/texts/${locale}?prefixes=${encodeURIComponent(textKeys)}`),
  });
  const [page, setPage] = useState<Page | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [closeFailed, setCloseFailed] = useState(false);
  const [human, setHuman] = useState(0);
  const [agent, setAgent] = useState(0);
  const [title, setTitle] = useState("");
  const L = (key: string) => texts.data?.[key] ?? `⟦${key}⟧`;
  useEffect(() => {
    let alive = true;
    portalFetch<Page>(`/api/portal/chats/${id}/participants`)
      .then((value) => {
        if (!alive) return;
        setPage(value);
        setStatus(200);
        setTitle(value.title);
        setHuman(value.humans[0]?.id ?? 0);
        setAgent(value.agents[0]?.id ?? 0);
      })
      .catch((failure: unknown) => {
        if (!alive) return;
        if (failure instanceof PortalHttpError) {
          setStatus(failure.status);
          if (failure.status === 403) setPage(failure.body as Page);
        } else setStatus(502);
      });
    return () => {
      alive = false;
    };
  }, [id]);
  useEffect(() => {
    if (texts.data) document.title = `${L("GRP_MANAGE_TITLE")}: ${page?.title ?? ""}`;
  });
  useEnhancedLoad(page, notice, error);
  // La página del portal actual es Static SSR: al cargar, FocusOnNavigate enfoca el título y el primer Tab sigue después
  // de él (los participantes, o el botón Chats si la persona no administra el grupo).
  useFocusHeading(!!texts.data && status !== null);
  if (!texts.data || status === null) return null;
  const canEdit = status === 200 && !!page?.canManage && page.status === "OPEN";
  const failureText = (code: string | null | undefined) =>
    L(
      code === "FORBIDDEN"
        ? "GRP_ERR_FORBIDDEN"
        : code === "PARTICIPANT_NOT_ALLOWED"
          ? "GRP_ERR_PARTICIPANT"
          : code === "ALREADY_PARTICIPANT"
            ? "GRP_ERR_ALREADY"
            : code === "INVALID_TITLE"
              ? "GRP_ERR_TITLE"
              : code === "CLOSED"
                ? "GRP_ERR_CLOSED"
                : code === "CANNOT_MANAGE"
                  ? "GRP_CANNOT_MANAGE"
                  : code === "NOT_FOUND"
                    ? "CHAT_NOT_FOUND"
                    : "GRP_ERR_SAVE",
    );
  const save = async (operation: string, body: Record<string, unknown>, successKey: string) => {
    setTitleError(null);
    setCloseFailed(false);
    let result: Result;
    try {
      result = await portalFetch<Result>(`/api/portal/chats/${id}/participants/${operation}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": getAntiforgeryToken() ?? "" },
        body: JSON.stringify(body),
      });
    } catch (failure) {
      result =
        failure instanceof PortalHttpError && failure.body && typeof failure.body === "object"
          ? (failure.body as Result)
          : { saved: false, failureCode: "SAVE_FAILED" };
    }
    if (result.page) {
      setPage(result.page);
      // Un nombre rechazado se conserva escrito; si se guardó (o fue otro cambio) vale el del servidor.
      if (operation !== "rename" || result.saved) setTitle(result.page.title);
      setHuman(result.page.humans[0]?.id ?? 0);
      setAgent(result.page.agents[0]?.id ?? 0);
    }
    if (result.saved) {
      setNotice(L(successKey));
      setError(null);
      // Cerrar lleva al chat del grupo, como la página después del POST.
      if (operation === "close") {
        location.assign(`/portal/chats/${id}`);
        return;
      }
    } else {
      setNotice(null);
      const text = failureText(result.failureCode);
      setError(text);
      if (result.failureCode === "INVALID_TITLE") setTitleError(text);
      if (operation === "close") setCloseFailed(true);
    }
    // La página del portal actual se vuelve a pedir: el foco queda en la página y la vista arriba.
    (document.activeElement as HTMLElement | null)?.blur();
    document.querySelector("main#main")?.scrollTo({ top: 0 });
  };
  const forbidden = status === 403 && !page;
  const missing = status === 404 || (status !== 200 && status !== 403);
  const heading = `${L("GRP_MANAGE_TITLE")}: ${page?.title ?? ""}`;
  return (
    <>
      <MobileBack href={`/portal/chats/${id}`} label={page?.title ?? L("CHAT_TITLE")} alwaysVisible />
      <h1>{heading}</h1>
      {forbidden ? (
        <SectionMessage tone="error" message={L("TEAM_ERR_FORBIDDEN")} />
      ) : missing || !page ? (
        <SectionMessage tone="error" message={error ?? L("CHAT_NOT_FOUND")} />
      ) : !page.canManage ? (
        <SectionMessage tone="error" message={L("GRP_CANNOT_MANAGE")} />
      ) : page.status === "CLOSED" ? (
        <SectionMessage message={L("GRP_ERR_CLOSED")} />
      ) : null}
      {notice !== null ? <SectionMessage tone="success" message={notice} /> : null}
      {page && error !== null && (!canEdit || (titleError === null && !closeFailed)) ? (
        <SectionMessage tone="error" message={error} />
      ) : null}
      {"\n\n"}
      <section className="card" aria-label={L("GRP_PARTICIPANTS")} hidden={forbidden || page?.canManage !== true}>
        <h2>{L("GRP_PARTICIPANTS")}</h2>
        {"\n    "}
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const button = (event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
            void save("remove", { participantId: Number(button?.value ?? 0) }, "GRP_REMOVED");
          }}
        >
          <div className="rows">
            {(page?.participants ?? []).map((participant) => (
              <div className="rowline" key={participant.participantId}>
                {"\n                    "}
                <div>
                  <strong>{participant.name}</strong>
                  <span className="trace">
                    {participant.kind === "AGENT" ? L("GRP_KIND_AGENT") : L("GRP_KIND_HUMAN")}
                    {participant.role === "OWNER" ? " · " + L("GRP_ROLE_OWNER") : ""}
                  </span>
                </div>
                {canEdit ? (
                  <button
                    type="submit"
                    name="removeForm.ParticipantId"
                    value={participant.participantId}
                    className="button ghost"
                    aria-label={`${L("GRP_REMOVE")}: ${participant.name}`}
                  >
                    {L("GRP_REMOVE")}
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </form>
        {"\n    "}
        <form
          className="inline"
          hidden={!canEdit || (page?.humans.length ?? 0) === 0}
          onSubmit={(event) => {
            event.preventDefault();
            void save("add-human", { referenceId: human }, "GRP_ADDED");
          }}
        >
          <div className="field">
            {"\n            "}
            <label htmlFor="grp-human">{L("GRP_ADD_HUMAN")}</label>
            {"\n            "}
            <select
              id="grp-human"
              name="humanForm.ReferenceId"
              disabled={!canEdit || (page?.humans.length ?? 0) === 0}
              value={human}
              onChange={(event) => setHuman(Number(event.target.value))}
            >
              {(page?.humans ?? []).map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
          </div>
          {"\n        "}
          <button type="submit" className="button secondary" disabled={!canEdit || (page?.humans.length ?? 0) === 0}>
            {L("GRP_ADD")}
          </button>
        </form>
        {"\n    "}
        <form
          className="inline"
          hidden={!canEdit || (page?.agents.length ?? 0) === 0}
          onSubmit={(event) => {
            event.preventDefault();
            void save("add-agent", { referenceId: agent }, "GRP_ADDED");
          }}
        >
          <div className="field">
            {"\n            "}
            <label htmlFor="grp-agent">{L("GRP_ADD_AGENT")}</label>
            {"\n            "}
            <select
              id="grp-agent"
              name="agentForm.ReferenceId"
              disabled={!canEdit || (page?.agents.length ?? 0) === 0}
              value={agent}
              onChange={(event) => setAgent(Number(event.target.value))}
            >
              {(page?.agents ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </select>
          </div>
          {"\n        "}
          <button type="submit" className="button secondary" disabled={!canEdit || (page?.agents.length ?? 0) === 0}>
            {L("GRP_ADD")}
          </button>
        </form>
      </section>
      {"\n"}
      <section className="card" aria-label={L("GRP_SETTINGS")} hidden={!canEdit && !closeFailed}>
        <h2>{L("GRP_SETTINGS")}</h2>
        {"\n    "}
        <form
          className="inline"
          hidden={!canEdit}
          onSubmit={(event) => {
            event.preventDefault();
            void save("rename", { title }, "GRP_RENAMED");
          }}
        >
          {titleError !== null ? (
            <ErrorSummary
              id="grp-rename-errors"
              title={L("GRP_NAME")}
              issues={[{ fieldId: "grp-title", message: titleError }]}
            />
          ) : null}
          <div className="field">
            {"\n            "}
            <label htmlFor="grp-title">{L("GRP_NAME")}</label>
            {"\n            "}
            <input
              type="text"
              id="grp-title"
              name="titleForm.Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              minLength={2}
              maxLength={200}
              aria-invalid={titleError !== null ? "true" : "false"}
              aria-describedby={titleError === null ? undefined : "grp-title-error"}
            />
            {"\n            "}
            <FieldError id="grp-title-error" message={titleError} />
          </div>
          {"\n        "}
          <button type="submit" className="button secondary" disabled={!canEdit}>
            {L("GRP_RENAME")}
          </button>
        </form>
        {"\n    "}
        <a
          id="grp-close-confirm-trigger"
          href={`${location.href.split("#")[0]}#grp-close-confirm`}
          className="button secondary"
          data-confirm-open="grp-close-confirm"
        >
          {L("GRP_CLOSE")}
        </a>
        <dialog
          id="grp-close-confirm"
          open
          className="confirmation-dialog"
          role="dialog"
          aria-labelledby="grp-close-confirm-title"
          aria-describedby="grp-close-confirm-description"
          data-confirm-dialog=""
          data-keep-open={closeFailed ? "true" : "false"}
          data-confirm-completion="0"
        >
          <h2 id="grp-close-confirm-title">{L("GRP_CLOSE_TITLE")}</h2>
          {"\n    "}
          <p id="grp-close-confirm-description">{L("GRP_CLOSE_DESCRIPTION")}</p>
          {closeFailed && error ? <SectionMessage message={error} tone="error" /> : null}
          <div className="confirmation-actions">
            {"\n        "}
            <a
              href={`${location.href.split("#")[0]}#grp-close-confirm-trigger`}
              className="button secondary confirmation-cancel"
              data-confirm-close=""
            >
              {L("ACTION_CANCEL")}
            </a>
            {"\n        "}
            <form
              className="inline"
              onSubmit={(event) => {
                event.preventDefault();
                void save("close", {}, "GRP_CLOSED");
              }}
            >
              <button type="submit" className="button secondary" disabled={!canEdit}>
                {L("GRP_CLOSE")}
              </button>
            </form>
          </div>
        </dialog>
      </section>
    </>
  );
}
